import { useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/utils/format';
import type { Wallet } from '@/types/portal';
import { PLATFORM } from '@/config/platform';

/** Where agencies ask Twiva to credit their wallet (Twiva admins top it up from the pool wallet). */
const FUNDING_EMAIL = import.meta.env.VITE_FUNDING_CONTACT_EMAIL || 'info@twiva.co.ke';

interface FundingHelpModalProps {
  open: boolean;
  wallet: Wallet | null;
  onClose: () => void;
}

/**
 * Shown instead of the top-up form on the production API, which has no way for an agency
 * to fund its own wallet: Twiva credits it from the admin panel once the agency has paid.
 */
export default function FundingHelpModal({ open, wallet, onClose }: FundingHelpModalProps) {
  const [copied, setCopied] = useState(false);

  const details = wallet
    ? `Wallet: ${wallet.ownerName}\nWallet ID: ${wallet.id}\nCurrency: ${wallet.currency}\nAmount to add: `
    : 'Wallet: \nAmount to add: ';
  const mailto = `mailto:${FUNDING_EMAIL}?subject=${encodeURIComponent(
    `Wallet top-up request${wallet ? ` · ${wallet.ownerName}` : ''}`,
  )}&body=${encodeURIComponent(`Hello ${PLATFORM.name},\n\nPlease top up our CPaaS wallet.\n\n${details}\n\nPayment reference: \n`)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-safe-2-line"
      title="Fund your wallet"
      subtitle={`${PLATFORM.name} adds the money to your wallet for you`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button icon="ri-mail-send-line" onClick={() => (window.location.href = mailto)}>
            Email {PLATFORM.name}
          </Button>
        </>
      }
    >
      <ol className="space-y-3 text-sm text-foreground-700">
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">1</span>
          <span>
            Pay {PLATFORM.name} the amount you want to add, the way your agreement with {PLATFORM.name} sets out.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">2</span>
          <span>
            Email <span className="font-medium text-foreground-900">{FUNDING_EMAIL}</span> with the wallet details
            below, the amount and your payment reference.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">3</span>
          <span>
            Once {PLATFORM.name} credits the wallet, the top-up appears in its history. Press <span className="font-medium">Refresh</span>{' '}
            to see the new balance.
          </span>
        </li>
      </ol>

      {wallet ? (
        <div className="mt-5 rounded-md border border-background-200 bg-background-100 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
              <dt className="text-foreground-500">Wallet</dt>
              <dd className="truncate text-foreground-900">{wallet.ownerName}</dd>
              <dt className="text-foreground-500">Wallet ID</dt>
              <dd className="text-foreground-900">{wallet.id}</dd>
              <dt className="text-foreground-500">Balance</dt>
              <dd className="text-foreground-900">{formatMoney(wallet.balance, wallet.currency)}</dd>
            </dl>
            <Button size="sm" variant="outline" icon={copied ? 'ri-check-line' : 'ri-file-copy-line'} onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-5 text-xs text-foreground-500">This agency has no wallet yet. Create it first, then ask {PLATFORM.name} to fund it.</p>
      )}
    </Modal>
  );
}
